import { Link } from 'react-router-dom';
import logo from "@/assets/logo.png";
import { footerLinks } from '@/constants/navigation';
import { socialLinks, contactInfo } from '@/constants/contact';

/**
 * Footer - Global footer component with links, social media, and contact information.
 */
const Footer = () => {
  return (
    <footer className="bg-dark relative overflow-hidden">
      {/* Decorative top gradient */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent"></div>
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full filter blur-[100px] pointer-events-none"></div>
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-accent/5 rounded-full filter blur-[80px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main footer grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 py-16">
          {/* Brand column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-5 group">
              <img src={logo} alt="NFskills" className="h-9 w-9 object-contain" />
              <span className="text-xl font-heading font-black text-white tracking-tight">
                NF<span className="text-primary">skills</span>
              </span>
            </Link>
            <p className="text-sm text-gray-400 leading-relaxed mb-6 max-w-xs">
              Hands-on IT training with real projects and expert mentors, helping you break into the tech industry with confidence.
            </p>

            {/* Social icons */}
            <div className="flex items-center gap-2">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className={`w-9 h-9 rounded-lg bg-white/6 border border-white/8 flex items-center justify-center text-gray-400 hover:text-white transition-all duration-200 ${social.color}`}
                >
                  <i className={`${social.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Dynamic Link Columns */}
          {footerLinks.map((group) => (
            <div key={group.title}>
              <h5 className="text-sm font-bold text-white uppercase tracking-widest mb-5">{group.title}</h5>
              <ul className="space-y-3">
                {group.links.map((item) => (
                  <li key={item.label}>
                    <Link
                      to={item.path}
                      className="text-sm text-gray-400 hover:text-primary transition-colors duration-200 flex items-center gap-2 group"
                    >
                      <span className="w-0 h-px bg-primary group-hover:w-3 transition-all duration-200"></span>
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact column */}
          <div>
            <h5 className="text-sm font-bold text-white uppercase tracking-widest mb-5">Get In Touch</h5>
            <ul className="space-y-4">
              {contactInfo.slice(0, 3).map((info, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${info.icon} text-primary text-sm`}></i>
                  </div>
                  {info.href ? (
                    <a href={info.href} className="text-sm text-gray-400 hover:text-primary transition-colors leading-relaxed">
                      {info.value}
                    </a>
                  ) : (
                    <span className="text-sm text-gray-400 leading-relaxed">{info.value}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            © 2018–{new Date().getFullYear()} NFskills. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="text-xs text-gray-500 hover:text-primary transition-colors">Privacy Policy</Link>
            <Link to="/faq" className="text-xs text-gray-500 hover:text-primary transition-colors">FAQ</Link>
            <Link to="/contact" className="text-xs text-gray-500 hover:text-primary transition-colors">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;